import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Mail, Search, Undo2, UserX, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "./api";
import { Badge, btnGhost, ConfirmButton, inputCls, Spinner, fmtDateTime } from "./ui";

const STATUS_LABELS = { new: "Νέο", done: "Ολοκληρώθηκε" };
const STATUS_STYLES = {
    new: "border-[#F7EAB8]/40 bg-[#F7EAB8]/10 text-[#F7EAB8]",
    done: "border-[#5FBBAC]/40 bg-[#5FBBAC]/10 text-[#5FBBAC]",
};

const FILTERS = [
    { id: "", label: "Όλα" },
    { id: "new", label: "Νέα" },
    { id: "done", label: "Ολοκληρωμένα" },
];

const DeletionBadge = ({ status }) => (
    <Badge testId={`deletion-status-${status}`} className={STATUS_STYLES[status] || STATUS_STYLES.new}>
        {STATUS_LABELS[status] || status}
    </Badge>
);

export const DeletionRequestsPage = () => {
    const [items, setItems] = useState(null);
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);

    const load = useCallback(() => {
        const params = {};
        if (status) params.status = status;
        if (q.trim()) params.q = q.trim();
        api.get("/api/admin/deletion-requests", { params })
            .then((r) => setItems(r.data))
            .catch((e) => {
                toast.error(apiError(e));
                setItems([]);
            });
    }, [status, q]);

    useEffect(() => {
        const t = setTimeout(load, q ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    const setReqStatus = async (r, next) => {
        try {
            const res = await api.patch(`/api/admin/deletion-requests/${r.id}`, { status: next });
            setItems((list) => list.map((x) => (x.id === r.id ? res.data : x)));
            setSelected((s) => (s?.id === r.id ? res.data : s));
            toast.success("Η κατάσταση ενημερώθηκε.");
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const remove = async (r) => {
        try {
            await api.delete(`/api/admin/deletion-requests/${r.id}`);
            toast.success("Το αίτημα διαγράφηκε.");
            setSelected(null);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    return (
        <div className="mx-auto max-w-6xl" data-testid="admin-deletions-page">
            <h1 className="text-2xl font-bold text-[#F2F5F6]">Αιτήματα διαγραφής</h1>
            <p className="mt-1 text-sm text-[#697B80]">Αιτήματα διαγραφής λογαριασμού από τη φόρμα υποστήριξης του site.</p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap gap-2" data-testid="deletion-status-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setStatus(f.id)}
                            className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors duration-200 ${
                                status === f.id ? "border-[#22A6C0] bg-[#22A6C0]/15 text-[#4CC9DC]" : "border-white/10 text-[#A3B3B8] hover:border-white/25"
                            }`}
                            data-testid={`deletion-filter-${f.id || "all"}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="relative min-w-[200px] flex-1">
                    <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#697B80]" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Αναζήτηση σε όνομα, email ή λόγο…"
                        className={`${inputCls} pl-10`}
                        data-testid="deletion-search"
                    />
                </div>
            </div>

            {items === null ? (
                <Spinner testId="deletions-loading" />
            ) : items.length === 0 ? (
                <div className="mt-16 flex flex-col items-center gap-3 text-center" data-testid="deletions-empty">
                    <UserX size={28} className="text-[#697B80]" />
                    <p className="text-sm text-[#697B80]">Δεν υπάρχουν αιτήματα διαγραφής.</p>
                </div>
            ) : (
                <ul className="mt-6 space-y-3" data-testid="deletions-list">
                    {items.map((r) => (
                        <li key={r.id}>
                            <button
                                onClick={() => setSelected(r)}
                                className="w-full rounded-2xl border border-white/10 bg-[#093448] p-4 text-left transition-colors duration-200 hover:border-[#22A6C0]/40"
                                data-testid={`deletion-row-${r.id}`}
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-sm font-bold text-[#F2F5F6]">{r.name}</p>
                                    <DeletionBadge status={r.status} />
                                </div>
                                <p className="mt-1 text-xs text-[#4CC9DC]">{r.email}</p>
                                {r.reason && <p className="mt-2 line-clamp-2 text-sm text-[#A3B3B8]">{r.reason}</p>}
                                <p className="mt-2 text-[11px] text-[#697B80]">{fmtDateTime(r.created_at)}</p>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {/* Detail modal */}
            {selected && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="deletion-modal">
                    <div className="mx-auto my-8 max-w-2xl rounded-3xl border border-white/10 bg-[#052536] p-6 md:p-8">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-xl font-bold text-[#F2F5F6]" data-testid="deletion-modal-name">{selected.name}</h2>
                                <p className="mt-0.5 text-sm text-[#4CC9DC]">{selected.email}</p>
                            </div>
                            <button onClick={() => setSelected(null)} className="rounded-full border border-white/10 p-2 text-[#A3B3B8] hover:text-white" aria-label="Κλείσιμο" data-testid="deletion-close">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <DeletionBadge status={selected.status} />
                            <span className="text-[11px] text-[#697B80]">{fmtDateTime(selected.created_at)}</span>
                        </div>

                        <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-[#697B80]">Λόγος διαγραφής</p>
                        <p className="mt-2 whitespace-pre-wrap rounded-2xl border border-white/5 bg-[#093448] p-4 text-sm leading-relaxed text-[#F2F5F6]" data-testid="deletion-modal-reason">
                            {selected.reason || "—"}
                        </p>

                        <div className="mt-6 flex flex-wrap gap-2">
                            <a
                                href={`mailto:${selected.email}?subject=${encodeURIComponent("TheraOffice — Αίτημα διαγραφής λογαριασμού")}`}
                                className="inline-flex items-center gap-2 rounded-full bg-[#22A6C0] px-4 py-2 text-xs font-bold text-[#052536] transition-colors hover:bg-[#4CC9DC]"
                                data-testid="deletion-reply-email"
                            >
                                <Mail size={14} /> Επικοινωνία με email
                            </a>
                            {selected.status !== "done" && (
                                <button onClick={() => setReqStatus(selected, "done")} className={btnGhost} data-testid="deletion-mark-done">
                                    <CheckCircle2 size={14} /> Σήμανση ως ολοκληρωμένο
                                </button>
                            )}
                            {selected.status !== "new" && (
                                <button onClick={() => setReqStatus(selected, "new")} className={btnGhost} data-testid="deletion-mark-new">
                                    <Undo2 size={14} /> Σήμανση ως νέο
                                </button>
                            )}
                            <ConfirmButton onConfirm={() => remove(selected)} label="Διαγραφή" testId="deletion-delete" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
